import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react'
import { Dropdown, Form, OverlayTrigger, Popover } from 'react-bootstrap' // Import Spinner, OverlayTrigger, and Popover
import Select from 'react-select'
import { supabase } from '../../supabaseClient' // Import Supabase client
import GroceryExportMorePopup from '../common/GroceryExportMorePopup' // Import GroceryExportMorePopup component
import ConfirmationPopup from '../common/ConfirmationPopup' // Import ConfirmationPopup
import EditPopup from '../common/EditPopup' // Import EditPopup
import AddTaskPopUp from '../common/AddTaskPopup' // Import AddTaskPopUp
import DeleteCategory from '../common/DeleteCategory' // Import DeleteCategory
import PortalDropdown from '../common/PortalDropdown'
import QuickSharePopup from '../common/QuickSharePopup'
import UnsharePopup from '../common/UnsharePopup'
import { useMedia } from '../../Context/MediaContext';
import { TbFilterCog } from 'react-icons/tb';
import '../../assets/css/checklist.css';

const GroceryList = forwardRef(({ isDarkMode }, ref) => {
  const { uploadFile, uploading: mediaUploading } = useMedia();
  const fileInputRef = useRef(null);
  const [UserId, setUserId] = useState(null)
  const [tasks, setTasks] = useState([]) // State to track tasks
  const [completedTasks, setCompletedTasks] = useState({}) // State to track completed tasks
  const [hasMoreTasks, setHasMoreTasks] = useState(false) // State to track if there are more than 5 tasks
  const [searchTerm, setSearchTerm] = useState('') // State to track search term
  const [categories, setCategories] = useState([])
  const [subcategories, setSubcategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedSubcategory, setSelectedSubcategory] = useState('') // Define selectedSubcategory state
  const [showExportMore, setShowExportMore] = useState(false) // State to show ExportMorePopup
  const [allTasks, setAllTasks] = useState([]) // State to track all tasks
  const [showPopover, setShowPopover] = useState(null) // Add state to manage popover visibility
  const [showConfirmation, setShowConfirmation] = useState(false) // Add state to manage confirmation popup visibility
  const [taskToRemove, setTaskToRemove] = useState(null) // Add state to track task to be removed
  const [showEditPopup, setShowEditPopup] = useState(false) // Add state to manage edit popup visibility
  const [taskToEdit, setTaskToEdit] = useState(null) // Add state to track task to be edited
  const [showAddPopup, setShowAddPopup] = useState(false) // State for add item popup
  const [showDeleteCategory, setShowDeleteCategory] = useState(false) // State for delete category popup
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [quickShareState, setQuickShareState] = useState(null)
  const [unshareState, setUnshareState] = useState(null)
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false); // State for expand/collapse
  const [useFiltersInPopup, setUseFiltersInPopup] = useState(false); // Track if filters should be passed to popup
  const searchTimeoutRef = useRef(null); // For debounced search
  const [showFilters, setShowFilters] = useState(false);
  const [sharedGroceries, setSharedGroceries] = useState([])
  const [sharedCategories, setSharedCategories] = useState([])
  const [sentGroceryIds, setSentGroceryIds] = useState(new Set())
  const [sentCategoryIds, setSentCategoryIds] = useState(new Set())

  // Move confirmation states
  const [showMoveConfirmation, setShowMoveConfirmation] = useState(false);
  const [itemToMove, setItemToMove] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);

  const popoverRef = useRef(null)
  
  // Expose triggerShowAll function to parent
  useImperativeHandle(ref, () => ({
    triggerShowAll: () => {
      fetchAllTasks(searchTerm, selectedCategory, selectedSubcategory)
      setShowExportMore(true)
    }
  }))
  useEffect(() => {
    let getUser = async () => {
      const {
        data: { user }
      } = await supabase.auth.getUser()
      setUserId(user.id)
    }
    getUser()
  }, [])
  // Fetch shared groceries received by this user
  const fetchSharedGroceries = useCallback(async () => {
    if (!UserId) return
    try {
      const { data: shared } = await supabase
        .from('grocery_table')
        .select('id, title, description, status, grocery_table_id, shared_by, share_visible')
        .eq('user_id', UserId)
        .eq('is_share', true)
        .not('shared_by', 'is', null)
        .order('created_at', { ascending: false })

      const items = (shared || []).map(t => ({
        ...t, _shared: true, _sharedFrom: t.shared_by || 'Unknown', _isNew: t.share_visible === false
      }))
      setSharedGroceries(items)

      const catIds = [...new Set(items.map(t => t.grocery_table_id).filter(id => id && id !== 0))]
      if (catIds.length > 0) {
        const { data: cats } = await supabase
          .from('grocery_categories')
          .select('id, name, parent_category_id')
          .in('id', catIds)
        const parentIds = (cats || []).map(c => c.parent_category_id).filter(id => id && !catIds.includes(id))
        let parentCats = []
        if (parentIds.length > 0) {
          const { data } = await supabase.from('grocery_categories').select('id, name').in('id', [...new Set(parentIds)])
          parentCats = data || []
        }
        const allCatMap = {}
        ;[...(cats || []), ...parentCats].forEach(c => { allCatMap[c.id] = c })
        const parents = []
        const seen = new Set()
        ;(cats || []).forEach(cat => {
          const pid = cat.parent_category_id || cat.id
          if (!seen.has(String(pid))) {
            seen.add(String(pid))
            const name = cat.parent_category_id ? (allCatMap[cat.parent_category_id]?.name || cat.name) : cat.name
            parents.push({ id: `shared_${pid}`, originalId: String(pid), name: `${name} (shared)` })
          }
        })
        setSharedCategories(parents)
      } else {
        setSharedCategories([])
      }
    } catch (e) { console.error('Error fetching shared groceries:', e) }
  }, [UserId])

  const fetchSentGroceryIds = useCallback(async () => {
    if (!UserId) return
    try {
      const API_URL = process.env.REACT_APP_ZOOMEY_API_URL || 'http://localhost:3000'
      const res = await fetch(`${API_URL}/api/sharing/sent-task-ids?user_id=${UserId}&section_type=grocery`)
      const data = await res.json()
      setSentGroceryIds(new Set((data?.sent_task_ids || []).map(String)))
      setSentCategoryIds(new Set((data?.sent_category_ids || []).map(String)))
    } catch (e) { console.error('Error fetching sent grocery ids:', e) }
  }, [UserId])

  const syncSharedGrocery = async (groceryId, updates) => {
    try {
      const API_URL = process.env.REACT_APP_ZOOMEY_API_URL || 'http://localhost:3000'
      const response = await fetch(`${API_URL}/api/sharing/sync-task`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id: groceryId, user_id: UserId, updates, section_type: 'grocery' }),
      })
      if (!response.ok) {
        const data = await response.json().catch(() => null)
        console.error('Error syncing shared grocery:', data?.error || response.statusText)
      }
    } catch (e) { console.error('Error syncing shared grocery:', e) }
  }

  // Function to fetch categories from Supabase
  const fetchCategories = async () => {
    if (!UserId) return // Guard: Don't fetch if UserId is not set
    try {
      const { data, error } = await supabase
        .from('grocery_categories')
        .select('id, name, is_share')
        .eq('user_id', UserId)
        .is('parent_category_id', null) // Fetch only categories where parent_category_id is null
        .or('is_share.is.null,is_share.eq.false')
      if (error) {
        throw error
      }
      setCategories(data)
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  // Function to fetch subcategories from Supabase based on selected category
  const fetchSubcategories = async categoryId => {
    if (!UserId) return // Guard: Don't fetch if UserId is not set
    try {
      const { data, error } = await supabase
        .from('grocery_categories')
        .select('id, name, is_share')
        .eq('user_id', UserId)
        .eq('parent_category_id', categoryId) // Fetch subcategories where parent_category_id matches selected category
        .or('is_share.is.null,is_share.eq.false')
      if (error) {
        throw error
      }
      setSubcategories(data)
      return data
    } catch (error) {
      console.error('Error fetching subcategories:', error)
    }
  }

  // Function to fetch tasks from Supabase
  const fetchTasks = useCallback(
    async (search = '', categoryId = '', subcategoryId = '') => {
      if (UserId) {
        setIsLoading(true);
        try {
          let query = supabase
            .from('grocery_table')
            .select('id, title, description, status, grocery_table_id', { count: 'exact' })
            .eq('user_id', UserId)
            .or('is_share.is.null,is_share.eq.false')
            .order('created_at', { ascending: false }) // Order by created_at in descending order
            .ilike('title', `%${search}%`)
            .limit(5)
  
          if (subcategoryId) {
            query = query.eq('grocery_table_id', subcategoryId)
          } else if (categoryId === '0' || categoryId === 0) {
            query = query.is('grocery_table_id', null)
          } else if (categoryId) {
            const { data: subcategories } = await supabase
              .from('grocery_categories')
              .select('id')
              .eq('parent_category_id', categoryId)
            const subcategoryIds = subcategories.map(sub => sub.id)
            query = query.in('grocery_table_id', [categoryId, ...subcategoryIds])
          }
  
          const { data, error, count } = await query
          if (error) {
            throw error
          }
          const sorted = data.sort((a, b) => {
            // Step 1: Prioritize 'pending' before 'completed'
            if (a.status !== b.status) {
              return a.status === 'pending' ? -1 : 1
            }
            // Step 2: If both are 'pending', sort by created_at (newest first)
            if (a.status === 'pending') {
              return new Date(b.created_at) - new Date(a.created_at)
            }
            // Step 3: If both are 'completed', keep original order
            return 0
          })
          setTasks(sorted)
          setHasMoreTasks(count > 5)
          const completed = {}
          data.forEach(task => {
            completed[task.id] = task.status === 'completed'
          })
          setCompletedTasks(completed)
        } catch (error) {
          console.error('Error fetching tasks:', error)
        } finally {
          setIsLoading(false);
        }
      }
    },
    [UserId]
  )

  // Function to fetch all tasks from Supabase
  const fetchAllTasks = async (
    search = '',
    categoryId = '',
    subcategoryId = ''
  ) => {
    try {
      let query = supabase
        .from('grocery_table')
        .select('id, title, description, status, grocery_table_id')
        .eq('user_id', UserId)
        .or('is_share.is.null,is_share.eq.false')
        .order('created_at', { ascending: false })
        .ilike('title', `%${search}%`)

      if (subcategoryId) {
        query = query.eq('grocery_table_id', subcategoryId)
      } else if (categoryId === '0' || categoryId === 0) {
        query = query.is('grocery_table_id', null)
      } else if (categoryId) {
        const { data: subcategoriesData } = await supabase
          .from('grocery_categories')
          .select('id')
          .eq('parent_category_id', categoryId)
        const subcategoryIds = (subcategoriesData || []).map(sub => sub.id)
        query = query.in('grocery_table_id', [categoryId, ...subcategoryIds])
      }

      const { data, error } = await query
      if (error) {
        throw error
      }
      setAllTasks(data)
    } catch (error) {
      console.error('Error fetching all tasks:', error)
    }
  }

  useEffect(() => {
    if (!UserId) return // Don't subscribe until UserId is available
    fetchCategories()
    fetchTasks()
    const taskSubscription = supabase
      .channel('public:grocery_table')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'grocery_table' },
        payload => {
          setTimeout(() => {
            fetchTasks(searchTerm, selectedCategory, selectedSubcategory)
          }, 2000)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(taskSubscription)
    }
  }, [UserId, fetchTasks, searchTerm, selectedCategory, selectedSubcategory])

  // Refresh shared groceries and sent grocery ids when userId is available
  useEffect(() => {
    if (UserId) { fetchSharedGroceries(); fetchSentGroceryIds(); }
  }, [UserId, fetchSharedGroceries, fetchSentGroceryIds])

  // Realtime subscription for shared groceries
  useEffect(() => {
    if (!UserId) return
    const channel = supabase
      .channel(`shared_grocery_${UserId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "grocery_table", filter: `user_id=eq.${UserId}` }, (payload) => {
        if (payload.new?.is_share) fetchSharedGroceries()
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "grocery_table", filter: `user_id=eq.${UserId}` }, (payload) => {
        if (payload.new?.is_share) fetchSharedGroceries()
        else fetchTasks(searchTerm, selectedCategory, selectedSubcategory)
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "grocery_table", filter: `user_id=eq.${UserId}` }, () => fetchSharedGroceries())
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [UserId, fetchSharedGroceries])

  const handleCategoryChange = e => {
    const categoryId = e.target.value
    setSelectedCategory(categoryId)
    fetchSubcategories(categoryId)
    fetchTasks(searchTerm, categoryId)
  }

  const handleSubcategoryChange = e => {
    const subcategoryId = e.target.value
    setSelectedSubcategory(subcategoryId)
    fetchTasks(searchTerm, selectedCategory, subcategoryId)
  }

  const handleSearchChange = e => {
    const search = e.target.value
    setSearchTerm(search)
    
    // Debounce the search to prevent blank page issues
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchTasks(search, selectedCategory, selectedSubcategory)
    }, 300)
  }
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  const toggleTaskCompletion = async (taskId, isShared = false) => {
    const sharedTask = isShared ? sharedGroceries.find(t => t.id === taskId) : null
    const isCurrentlyCompleted = isShared ? sharedTask?.status === 'completed' : completedTasks[taskId]
    const previousStatus = isShared ? (sharedTask?.status || 'pending') : null
    const newStatus = isCurrentlyCompleted ? 'pending' : 'completed'
    if (isShared) {
      setSharedGroceries(prev => prev.map(task =>
        task.id === taskId ? { ...task, status: newStatus } : task
      ))
    }
    try {
      const { error } = await supabase
        .from('grocery_table')
        .update({ status: newStatus })
        .eq('id', taskId)
        .eq('user_id', UserId)
      if (error) {
        throw error
      }
      if (!isShared) {
        setCompletedTasks(prev => ({
          ...prev,
          [taskId]: !prev[taskId]
        }))
      }
      await syncSharedGrocery(taskId, { status: newStatus })
    } catch (error) {
      if (isShared) {
        setSharedGroceries(prev => prev.map(task =>
          task.id === taskId ? { ...task, status: previousStatus } : task
        ))
      }
      console.error('Error updating task status:', error)
    }
  }

  const confirmRemoveTask = taskId => {
    setTaskToRemove(taskId)
    setShowConfirmation(true)
  }

  const removeTask = async () => {
    try {
      const { error } = await supabase
        .from('grocery_table')
        .delete()
        .eq('id', taskToRemove)
      if (error) {
        throw error
      }
      setTasks(prev => prev.filter(task => task.id !== taskToRemove))
      setShowConfirmation(false)
      setTaskToRemove(null)
    } catch (error) {
      console.error('Error removing task:', error)
    }
  }

  const handleClickOutside = event => {
    if (popoverRef.current && !popoverRef.current.contains(event.target)) {
      setShowPopover(null)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleEditTask = task => {
    setTaskToEdit({ ...task, category_id: task.grocery_table_id || null })
    setShowEditPopup(true)
  }

  const saveTask = async updatedTask => {
    try {
      let categoryId = updatedTask.category_id
      let subcategoryId = updatedTask.subcategory_id
      
      // Handle new category creation
      // Note: When isNewCategory is true, category_id contains the new category name (string)
      if (updatedTask.isNewCategory && categoryId && typeof categoryId === 'string' && categoryId.trim()) {
        const { data: newCat, error: catError } = await supabase
          .from('grocery_categories')
          .insert([{ name: categoryId.trim(), user_id: UserId, parent_category_id: null }])
          .select()
          .single()
        if (catError) throw catError
        categoryId = newCat.id
        fetchCategories() // Refresh categories list
      }
      
      // Handle new subcategory creation
      // Note: When isNewSubcategory is true, subcategory_id contains the new subcategory name (string)
      if (updatedTask.isNewSubcategory && subcategoryId && typeof subcategoryId === 'string' && subcategoryId.trim() && categoryId) {
        const { data: newSubcat, error: subcatError } = await supabase
          .from('grocery_categories')
          .insert([{ name: subcategoryId.trim(), user_id: UserId, parent_category_id: categoryId }])
          .select()
          .single()
        if (subcatError) throw subcatError
        subcategoryId = newSubcat.id
      }
      
      const updateData = {
        title: updatedTask.title,
        description: updatedTask.description,
        status: updatedTask.status || 'pending'
      }
      
      // Don't change category for shared/sent items
      if (!updatedTask._shared && !updatedTask._sent) {
        const finalCategoryId = subcategoryId || categoryId
        if (finalCategoryId === "0" || finalCategoryId === 0) {
          updateData.grocery_table_id = null
        } else if (finalCategoryId) {
          updateData.grocery_table_id = finalCategoryId
        }
      }

      if (updatedTask.id) {
        const { error } = await supabase
          .from('grocery_table')
          .update(updateData)
          .eq('id', updatedTask.id)
        if (error) throw error
        syncSharedGrocery(updatedTask.id, {
          title: updateData.title,
          description: updateData.description,
        })
      } else {
        const { error } = await supabase
          .from('grocery_table')
          .insert([{ ...updateData, user_id: UserId }])
        if (error) throw error
      }
      
      fetchTasks(searchTerm, selectedCategory, selectedSubcategory)
      setShowEditPopup(false)
      setShowAddPopup(false)
      setTaskToEdit(null)
    } catch (error) {
      console.error('Error saving grocery item:', error)
    }
  }

  const confirmMoveItem = (task, targetTable) => {
    setItemToMove(task);
    setMoveTarget(targetTable);
    setShowMoveConfirmation(true);
  };

  const moveItem = async () => {
    if (!itemToMove || !moveTarget) return;
    try {
      const { error: insertError } = await supabase
        .from(moveTarget)
        .insert([{
          title: itemToMove.title,
          description: itemToMove.description,
          status: itemToMove.status,
          user_id: UserId
        }]);
      if (insertError) throw insertError;

      await supabase.from('grocery_table').delete().eq('id', itemToMove.id);
      fetchTasks(searchTerm, selectedCategory, selectedSubcategory);
      setShowMoveConfirmation(false);
      setItemToMove(null);
      setMoveTarget(null);
    } catch (error) {
      console.error('Error moving item:', error);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = await uploadFile(file);
      if (url) {
        await saveTask({
          title: `Attached: ${file.name}`,
          description: `File URL: ${url}`,
          status: 'pending'
        });
      }
    }
  };

  // Merge own tasks + shared groceries (filter shared by selected category)
  const filteredShared = sharedGroceries.filter(st => {
    if (tasks.some(t => t.id === st.id)) return false
    if (!selectedCategory) return true
    if (selectedCategory === '0') return !st.grocery_table_id
    if (String(selectedCategory).startsWith('shared_')) {
      const origId = selectedCategory.replace('shared_', '')
      return String(st.grocery_table_id) === origId
    }
    return false
  })
  const mergedTasks = [
    ...tasks.map(t => ({ ...t, _sent: sentGroceryIds.has(String(t.id)) })),
    ...filteredShared
  ]

  // Filter tasks based on search term
  const filteredTasks = mergedTasks.filter(
    task =>
      task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const formatMessage = text => {
    if (!text) return ''
    let cleanedText = text
      .replace(/^"/, '') // Remove leading quotation mark
      .replace(/"$/, '') // Remove trailing quotation mark
      .replace(/\\n/g, '') // Remove all escaped newlines
    return (
      cleanedText
        .replace(/###\s*(.*?)(?:\n|$)/g, '<h5>$1</h5>') // H3 headings
        .replace(/##\s*(.*?)(?:\n|$)/g, '<h4>$1</h4>') // H2 headings
        .replace(/#\s*(.*?)(?:\n|$)/g, '<h3>$1</h3>') // H1 headings
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold text
        .replace(/(\d+)\.\s+(.*?)(?:\n|$)/g, '<ol start="$1"><li>$2</li></ol>') // Numbered lists
        .replace(
          /`(.*?)`/g,
          '<code style="background-color: #f0f0f0; padding: 2px 4px; border-radius: 4px; font-family: monospace;">$1</code>'
        ) // Inline code
        .replace(
          /\[(.*?)\]\((https?:\/\/[^\s]+)\)/g,
          '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
        )
        .replace(/(^|[^"'>])(https?:\/\/[^\s<]+)/g,
          '$1<a href="$2" target="_blank" rel="noopener noreferrer">$2</a>'
        )
        .replace(/\{(.*?)\}/g, '$1')
    ) // Remove curly braces
  }

  const renderTask = (taskId, title, description, grocery_table_id, isShared = false, sharedFrom = '', isNew = false, isSent = false) => {
    const sharedTask = isShared ? sharedGroceries.find(t => t.id === taskId) : null;
    const isCompleted = isShared ? sharedTask?.status === 'completed' : completedTasks[taskId];

    return (
      <div
        className={`grocery-card ${isCompleted ? 'grocery-completed' : ''}`}
        key={`${isShared ? 'shared_' : ''}${taskId}`}
        data-item-id={taskId}
        onClick={e => {
          if (openDropdownId === taskId) return;
          handleEditTask({ id: taskId, title, description, category_id: grocery_table_id, _shared: isShared, _sent: isSent });
        }}
      >
        {/* Grocery Header Row */}
        <div className="grocery-card-header">
          <div
            className={`grocery-checkbox ${isCompleted ? 'checked' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleTaskCompletion(taskId, isShared);
            }}
          >
            {isCompleted && <i className="bi bi-check"></i>}
          </div>

          <div className="grocery-card-content">
            <div className="d-flex align-items-center gap-1">
              <h4 className={`grocery-title ${isCompleted ? 'completed' : ''}`} title={title} style={{ margin: 0 }}>
                {title}
              </h4>
              {isNew && (
                <span style={{
                  fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase',
                  background: 'var(--teal-bg)', color: '#fff',
                  padding: '1px 5px', borderRadius: '3px', flexShrink: 0, lineHeight: '13px',
                }}>New</span>
              )}
            </div>
            {description && (
              <p
                className={`grocery-description ${isCompleted ? 'completed' : ''}`}
                dangerouslySetInnerHTML={{ __html: formatMessage(description) }}
              />
            )}
          </div>

          {!isShared && (
            <i
              className="bi bi-send"
              style={{ fontSize: '0.85rem', cursor: 'pointer', color: '#14b8a6', padding: '2px 4px' }}
              title="Quick Share"
              onClick={(e) => { e.stopPropagation(); setQuickShareState(quickShareState?.id === taskId ? null : { id: taskId, rect: e.currentTarget.getBoundingClientRect() }) }}
            ></i>
          )}
          {quickShareState?.id === taskId && (
            <QuickSharePopup
              show={true}
              onClose={() => setQuickShareState(null)}
              itemId={taskId}
              sectionType="grocery"
              UserId={UserId}
              isDarkMode={isDarkMode}
              onShareSuccess={fetchSentGroceryIds}
              anchorRect={quickShareState?.rect}
            />
          )}
          {unshareState?.id === taskId && (
            <UnsharePopup
              show={true}
              onClose={() => setUnshareState(null)}
              itemId={taskId}
              sectionType="grocery"
              UserId={UserId}
              isDarkMode={isDarkMode}
              onUnshareSuccess={() => { fetchSentGroceryIds(); fetchTasks(searchTerm, selectedCategory, selectedSubcategory); }}
              anchorRect={unshareState?.rect}
            />
          )}
          <PortalDropdown
            align="end"
            menuItems={[
              {
                icon: 'bi bi-pencil',
                label: 'Edit',
                onClick: () => handleEditTask({ id: taskId, title, description, category_id: grocery_table_id, _shared: isShared, _sent: isSent })
              },
              ...(isSent ? [{
                icon: 'bi bi-share',
                label: 'Unshare',
                danger: true,
                onClick: () => { setUnshareState({ id: taskId, rect: document.querySelector(`[data-item-id="${taskId}"]`)?.getBoundingClientRect() }) },
              }] : []),
              {
                icon: 'bi bi-check2-square',
                label: 'Move to Tasks',
                onClick: () => confirmMoveItem({ id: taskId, title, description, status: isCompleted ? 'completed' : 'pending' }, 'tasks'),
                disabled: isShared || isSent,
                title: isShared ? 'No authority' : isSent ? 'Unshare to use' : '',
              },
              {
                icon: 'bi bi-journal-arrow-up',
                label: 'Move to Notes',
                onClick: () => confirmMoveItem({ id: taskId, title, description, status: isCompleted ? 'completed' : 'pending' }, 'notes'),
                disabled: isShared || isSent,
                title: isShared ? 'No authority' : isSent ? 'Unshare to use' : '',
              },
              {
                icon: 'bi bi-list-ul',
                label: 'Move to Lists',
                onClick: () => confirmMoveItem({ id: taskId, title, description, status: isCompleted ? 'completed' : 'pending' }, 'lists'),
                disabled: isShared || isSent,
                title: isShared ? 'No authority' : isSent ? 'Unshare to use' : '',
              },
              {
                icon: 'bi bi-trash',
                label: 'Remove',
                danger: true,
                onClick: () => confirmRemoveTask(taskId),
                disabled: isShared || isSent,
                title: isShared ? 'No authority' : isSent ? 'Unshare to use' : '',
              }
            ]}
          >
            <i
              className='bi bi-three-dots-vertical'
              onMouseDown={(e) => e.stopPropagation()}
            ></i>
          </PortalDropdown>
        </div>

        {/* Grocery Footer Row */}
        <div className="grocery-card-footer">
          <div className={`task-status-badge ${isCompleted ? 'status-completed' : 'status-pending'}`}>
            <i className={`bi ${isCompleted ? 'bi-check-circle-fill' : 'bi-clock'}`}></i>
            <span>{isCompleted ? 'Completed' : 'Pending'}</span>
          </div>

          {isShared && (
            <div
              title={`Received from ${sharedFrom} — Click to copy email`}
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(sharedFrom);
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: '3px',
                fontSize: '0.65rem', color: 'var(--teal-bg)', marginLeft: 'auto',
                cursor: 'pointer', padding: '1px 6px', borderRadius: '4px',
                background: 'rgba(var(--teal-bg-rag), 0.08)',
              }}
            >
              <i className="bi bi-box-arrow-in-down-right" style={{ fontSize: '0.7rem' }}></i>
            </div>
          )}

          {isSent && !isShared && (
            <div
              title="Shared to others"
              style={{
                display: 'flex', alignItems: 'center', gap: '3px',
                fontSize: '0.65rem', color: '#f59e0b', marginLeft: 'auto',
                padding: '1px 6px', borderRadius: '4px',
                background: 'rgba(245, 158, 11, 0.08)',
              }}
            >
              <i className="bi bi-box-arrow-up-right" style={{ fontSize: '0.7rem' }}></i>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="checklist-tabs-header" style={{ padding: '0 1.5em',paddingLeft:"36px", marginBottom: '10px' }}>
        <span style={{ fontWeight: '600', fontSize: '14px' }}>Grocery List</span>
        <button
          className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
          title={showFilters ? 'Hide filters' : 'Show filters'}
        >
          <TbFilterCog size={16} />
        </button>
      </div>
      {showFilters && (
        <>
          <div className='d-flex mb-3 gap-2' style={{ padding: "0em 1.5em" }}>
            <Select
              className='custom-select-container flex-fill'
              classNamePrefix='custom-select'
              options={[
                { value: '', label: 'All Categories' },
                { value: '0', label: 'General' },
                ...categories.map(category => ({
                  value: category.id,
                  label: category.name,
                  isReceived: category.is_share === true,
                  isSent: sentCategoryIds.has(String(category.id)),
                })),
                ...sharedCategories.map(sc => ({
                  value: sc.id,
                  label: sc.name.replace(' (shared)', ''),
                  isShared: true,
                }))
              ]}
              formatOptionLabel={(option) => (
                <div className="d-flex align-items-center gap-1">
                  {option.isReceived && <i className="bi bi-box-arrow-in-down-right" style={{ fontSize: '0.7rem', color: 'var(--teal-bg)' }}></i>}
                  {option.isSent && <i className="bi bi-box-arrow-up-right" style={{ fontSize: '0.7rem', color: '#f59e0b' }}></i>}
                  {option.isShared && <i className="bi bi-box-arrow-in-down-right" style={{ fontSize: '0.7rem', color: 'var(--teal-bg)' }}></i>}
                  <span>{option.label}</span>
                </div>
              )}
              value={
                selectedCategory === ''
                  ? { value: '', label: 'All Categories' }
                  : selectedCategory === '0' || selectedCategory === 0
                    ? { value: '0', label: 'General' }
                    : categories.find(c => String(c.id) === String(selectedCategory))
                      ? { value: selectedCategory, label: categories.find(c => String(c.id) === String(selectedCategory))?.name }
                      : sharedCategories.find(sc => sc.id === selectedCategory)
                        ? { value: selectedCategory, label: sharedCategories.find(sc => sc.id === selectedCategory)?.name.replace(' (shared)', ''), isShared: true }
                        : { value: '', label: 'All Categories' }
              }
              onChange={(option) => handleCategoryChange({ target: { value: option?.value || '' } })}
              isSearchable={false}
              placeholder="All Categories"
            />
            <Select
              className='custom-select-container flex-fill'
              classNamePrefix='custom-select'
              options={[
                { value: '', label: 'All Subcategories' },
                ...subcategories.map(subcategory => ({
                  value: subcategory.id,
                  label: subcategory.name
                }))
              ]}
              value={
                selectedSubcategory === ''
                  ? { value: '', label: 'All Subcategories' }
                  : subcategories.find(s => String(s.id) === String(selectedSubcategory))
                    ? { value: selectedSubcategory, label: subcategories.find(s => String(s.id) === String(selectedSubcategory))?.name }
                    : { value: '', label: 'All Subcategories' }
              }
              onChange={(option) => handleSubcategoryChange({ target: { value: option?.value || '' } })}
              isSearchable={false}
              isDisabled={!selectedCategory}
              placeholder="All Subcategories"
            />
            {categories && categories.length > 0 && (
              <button
                className='delete-category-btn'
                onClick={() => setShowDeleteCategory(true)}
              >
                <i className='bi bi-trash'></i>
              </button>
            )}
          </div>
          <div className='d-flex mb-3' style={{ padding: "0em 1.5em" }}>
            <Form.Control
              className='search-input-box'
              type='text'
              placeholder='Quick add to grocery...'
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  const title = e.target.value.trim();
                  e.target.value = '';
                  await saveTask({ title, description: '', status: 'pending' });
                }
              }}
            />
          </div>
          <div className='d-flex mb-3 gap-2 ' style={{ padding: "0em 1.5em" }}>
            <Form.Control
              className='search-input-box flex-grow-1'
              type='text'
              placeholder='Search...'
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <div className='d-flex gap-2'>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <button
                style={{
                  backgroundColor: "var(--background)",
                  color: "var(--text)",
                  borderRadius: '8px',
                  border: "1px solid var(--border-color)",
                  height: '38px',
                  width: '38px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={() => fileInputRef.current?.click()}
                disabled={mediaUploading}
              >
                {mediaUploading ? <i className='bi bi-hourglass-split'></i> : <i className='bi bi-paperclip'></i>}
              </button>
              <button
                style={{
                  backgroundColor: "var(--teal-bg)",
                  color: "var(--textwhite)",
                  borderRadius: '8px',
                  height: '38px',
                  width: '38px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={() => setShowAddPopup(true)}
              >
                <i className='bi bi-plus'></i>
              </button>
            </div>
          </div>
        </>
      )}
      <hr />
      <div className='d-flex flex-column'>
        <div className='task-list' style={{ padding: "0em 1.5em" }}>
          {isLoading ? (
            <div className="wave-loader-container">
              <div className="wave-loader">
                <div className="wave-bar"></div>
                <div className="wave-bar"></div>
                <div className="wave-bar"></div>
                <div className="wave-bar"></div>
                <div className="wave-bar"></div>
              </div>
              <span className="wave-loader-text">Loading groceries...</span>
            </div>
          ) : filteredTasks.length > 0 ? (
            filteredTasks.map(task =>
              renderTask(task.id, task.title, task.description, task.grocery_table_id, task._shared || false, task._sharedFrom || '', task._isNew || false, task._sent || false)
            )
          ) : (
            <div className='text-center'>No data found</div>
          )}
        </div>
        {(hasMoreTasks || filteredTasks.length > 0) && (
          <div className='d-flex justify-content-center mt-3 gap-3'>
            {filteredTasks.length > 0 && (
              <span
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setIsExpanded(!isExpanded)
                  if (!isExpanded) {
                    setUseFiltersInPopup(true) // Pass filters for Expand
                    fetchAllTasks(searchTerm, selectedCategory, selectedSubcategory)
                    setShowExportMore(true)
                  }
                }}
              >
                {isExpanded ? 'Collapse' : 'Expand'}
              </span>
            )}
            {hasMoreTasks && (
              <span
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setUseFiltersInPopup(false) // No filters for Show All
                  fetchAllTasks('', '', '')
                  setShowExportMore(true)
                }}
              >
                Show All
              </span>
            )}
          </div>
        )}
      </div>
      {UserId && (
        <GroceryExportMorePopup
          show={showExportMore}
          handleClose={() => {
            setShowExportMore(false)
            setIsExpanded(false)
            setUseFiltersInPopup(false)
          }}
          title='Grocery Items'
          items={allTasks}
          fetchItems={fetchAllTasks}
          isDarkMode={isDarkMode}
          UserId={UserId}
          initialSearchTerm={useFiltersInPopup ? searchTerm : ''}
          initialCategory={useFiltersInPopup ? selectedCategory : ''}
          initialSubcategory={useFiltersInPopup ? selectedSubcategory : ''}
        />
      )}
      <ConfirmationPopup
        show={showConfirmation}
        handleClose={() => setShowConfirmation(false)}
        title='Confirm Removal'
        body='Are you sure you want to remove this grocery item? This action cannot be undone.'
        handleConfirm={removeTask}
      />
      <ConfirmationPopup
        show={showMoveConfirmation}
        handleClose={() => {
          setShowMoveConfirmation(false);
          setItemToMove(null);
          setMoveTarget(null);
        }}
        title={`Move to ${moveTarget === 'tasks' ? 'Tasks' : moveTarget === 'notes' ? 'Notes' : 'Lists'}`}
        body={`Are you sure you want to move "${itemToMove?.title}" to ${moveTarget === 'tasks' ? 'Tasks' : moveTarget === 'notes' ? 'Notes' : 'Lists'}? The item will be removed from Groceries.`}
        handleConfirm={moveItem}
      />
      <EditPopup
        show={showEditPopup}
        handleClose={() => {
          setShowEditPopup(false)
          setTaskToEdit(null)
        }}
        handleSave={saveTask}
        item={taskToEdit}
        titleAction='Grocery'
        categoriesData={categories}
        fetchSubcategories={fetchSubcategories}
        subcategoriesData={subcategories}
        handleDelete={confirmRemoveTask}
        sharedCategories={sharedCategories}
      />
      {UserId && (
        <AddTaskPopUp
          actionTitle='Add Grocery Item'
          show={showAddPopup}
          handleClose={() => setShowAddPopup(false)}
          handleSave={saveTask}
          categoriesData={categories}
          fetchSubcategories={fetchSubcategories}
          subcategoriesData={subcategories}
          UserId={UserId}
          onTaskCreated={() => fetchTasks(searchTerm, selectedCategory, selectedSubcategory)}
          tableName="grocery_table"
        />
      )}
      <DeleteCategory
        show={showDeleteCategory}
        categpries={categories}
        handleClose={() => {
          setShowDeleteCategory(false)
          fetchCategories() // Refresh categories after deletion
        }}
        type='groceries'
      />
    </div>
  )
})

GroceryList.displayName = 'GroceryList'

export default GroceryList
